"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { authClient } from "@/lib/auth-client";
import { normalizePhoneNumber } from "@/lib/phone";

/** 로그인한 사람이 휴대폰 번호를 문자 인증으로 등록·변경한다. */
export function PhoneVerificationForm() {
  const router = useRouter();
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<{
    kind: "error" | "info";
    text: string;
  } | null>(null);

  async function sendCode() {
    const normalized = normalizePhoneNumber(phoneInput);
    if (!normalized) {
      setMessage({ kind: "error", text: "휴대폰 번호를 확인해 주세요." });
      return;
    }
    setPending(true);
    setMessage(null);
    const result = await authClient.phoneNumber.sendOtp({
      phoneNumber: normalized,
    });
    setPending(false);
    if (result.error) {
      setMessage({
        kind: "error",
        text: result.error.message || "인증번호를 보내지 못했습니다.",
      });
      return;
    }
    setPhoneNumber(normalized);
    setMessage({ kind: "info", text: "인증번호를 보냈습니다. 5분 안에 입력해 주세요." });
  }

  async function verifyCode() {
    if (!phoneNumber) return;
    setPending(true);
    setMessage(null);
    const result = await authClient.phoneNumber.verify({
      phoneNumber,
      code,
      updatePhoneNumber: true,
    });
    setPending(false);
    if (result.error) {
      setMessage({
        kind: "error",
        text:
          result.error.message ||
          "인증하지 못했습니다. 인증번호를 확인해 주세요.",
      });
      return;
    }
    setPhoneNumber(null);
    setCode("");
    setPhoneInput("");
    setMessage({ kind: "info", text: "휴대폰 번호를 인증했습니다." });
    router.refresh();
  }

  return (
    <FieldGroup className="gap-3">
      <Field>
        <FieldLabel htmlFor="account-phone">휴대폰 번호</FieldLabel>
        <div className="flex gap-2">
          <Input
            id="account-phone"
            type="tel"
            autoComplete="tel"
            placeholder="010-1234-5678"
            value={phoneInput}
            onChange={(event) => setPhoneInput(event.target.value)}
          />
          <Button
            type="button"
            variant="outline"
            onClick={sendCode}
            disabled={pending}
          >
            인증번호 받기
          </Button>
        </div>
        <FieldDescription>해외 번호는 +국가번호로 입력해 주세요.</FieldDescription>
      </Field>
      {phoneNumber && (
        <Field>
          <FieldLabel htmlFor="account-phone-code">인증번호</FieldLabel>
          <div className="flex gap-2">
            <Input
              id="account-phone-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
            <Button
              type="button"
              onClick={verifyCode}
              disabled={pending || code.length < 6}
            >
              확인
            </Button>
          </div>
        </Field>
      )}
      {message && (
        <Alert variant={message.kind === "error" ? "destructive" : "default"}>
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}
    </FieldGroup>
  );
}
